import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, apiFetch } from './utils/api';

const API_URL = API_BASE_URL;

function SettingsTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [templateType, setTemplateType] = useState('all'); // all, user, or sample

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`${API_URL}/api/templates`);
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await apiFetch(`${API_URL}/api/templates/${id}`, { method: 'DELETE' });
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await apiFetch(`${API_URL}/api/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${template.name} (Copy)`,
          description: template.description,
          layout_json: template.layout_json
        })
      });

      if (response.ok) {
        fetchTemplates();
        alert('Template duplicated successfully!');
      }
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Error duplicating template');
    }
  };

  // Rename a template in-place (no need to open the editor)
  const handleRename = async (template) => {
    const newName = window.prompt('Enter new template name:', template.name);
    if (!newName || !newName.trim() || newName.trim() === template.name) return;
    try {
      const response = await apiFetch(`${API_URL}/api/templates/${template.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (response.ok) {
        fetchTemplates();
      } else {
        const err = await response.json();
        alert('Rename failed: ' + (err.error || response.status));
      }
    } catch (error) {
      console.error('Error renaming template:', error);
      alert('Error renaming template');
    }
  };

  // Edit description in-place
  const handleEditDescription = async (template) => {
    const newDesc = window.prompt('Enter new description:', template.description || '');
    if (newDesc === null) return;
    try {
      const response = await apiFetch(`${API_URL}/api/templates/${template.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: newDesc.trim() }),
      });
      if (response.ok) {
        fetchTemplates();
      } else {
        const err = await response.json();
        alert('Update failed: ' + (err.error || response.status));
      }
    } catch (error) {
      console.error('Error editing description:', error);
      alert('Error editing description');
    }
  };

  // Toggle default assignment for an area
  const handleToggleDefault = async (template, area) => {
    const assignment = template.assignments?.find(a => a.area === area);
    const newDefault = !(assignment?.is_default);
    try {
      const response = await apiFetch(`${API_URL}/api/templates/${template.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ area, is_default: newDefault, is_active: true }),
      });
      if (response.ok) {
        fetchTemplates();
      } else {
        const err = await response.json();
        alert('Failed to set default: ' + (err.error || response.status));
      }
    } catch (error) {
      console.error('Error toggling default:', error);
      alert('Error setting default');
    }
  };

  // Export a template as a portable JSON file (for PPM library import on SM8)
  const handleExportTemplate = (template) => {
    const exportObj = {
      format: 'ppm-template',
      version: 1,
      exportedAt: new Date().toISOString(),
      source: 'doorbase',
      name: template.name,
      description: template.description || '',
      layout_json: template.layout_json,
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/[^a-z0-9]/gi, '_')}.ppm-template.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert(`Template "${template.name}" exported.\n\nTo import on the ADG portal:\n1. Open PPM → Template Builder\n2. Click 📚 Library → 📥 Import\n3. Upload this file`);
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (t.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesArea = filterArea === 'all' ||
                       (t.area === filterArea) ||
                       (t.assignments && t.assignments.some(a => a.area === filterArea));
    const type = t.template_type || 'user';
    const matchesType = templateType === 'all' ||
                       (templateType === 'user' && (type === 'user' || type === 'layout')) ||
                       type === templateType;
    return matchesSearch && matchesArea && matchesType;
  });

  const areaOptions = [
    { value: 'all', label: 'All Areas', color: 'gray' },
    { value: 'quote', label: 'Quotes', color: 'blue' },
    { value: 'invoice', label: 'Invoices', color: 'green' },
    { value: 'job', label: 'Jobs', color: 'orange' },
    { value: 'sales_order', label: 'Sales Orders', color: 'purple' },
    { value: 'delivery_note', label: 'Delivery Notes', color: 'pink' },
    { value: 'ppm_contract', label: 'PPM Contracts', color: 'indigo' }
  ];

  const getAreaColor = (area) => {
    const option = areaOptions.find(o => o.value === area);
    return option?.color || 'gray';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/reports-and-templates')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
              title="Back to Reports & Templates"
            >
              ⬅ Reports & Templates
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">Template Library</h1>
              <span className="text-sm text-blue-100">
                {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/settings/templates/new')}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
              >
              ➕ Create Template
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-6 pb-96">
        {/* Template Type Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setTemplateType('all')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                templateType === 'all'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📚 All Templates ({templates.length})
            </button>
            <button
              onClick={() => setTemplateType('user')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                templateType === 'user'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📁 My Templates ({templates.filter(t => {
                const type = t.template_type || 'user';
                return type === 'user' || type === 'layout';
              }).length})
            </button>
            <button
              onClick={() => setTemplateType('sample')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                templateType === 'sample'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ✨ Sample Templates ({templates.filter(t => t.template_type === 'sample').length})
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Area Filter */}
            <select
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {areaOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded transition ${
                  viewMode === 'grid'
                    ? 'bg-white shadow text-indigo-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                ⊞ Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded transition ${
                  viewMode === 'list'
                    ? 'bg-white shadow text-indigo-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                ☰ List
              </button>
            </div>
          </div>
        </div>

        {/* Templates */}
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchTerm || filterArea !== 'all' ? 'No templates found' : 'No templates yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterArea !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first template to get started'}
            </p>
            {!searchTerm && filterArea === 'all' && (
              <button
                onClick={() => navigate('/settings/templates/new')}
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition"
              >
                Create Template
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-96">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition overflow-hidden group"
              >
                {/* Preview */}
                <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
                  {template.layout_json?.components?.length > 0 ? (
                    <div className="relative w-full h-full flex items-center justify-center p-4">
                      <div
                        className="bg-white shadow-lg"
                        style={{
                          width: '160px',
                          height: '226px',
                          transform: 'scale(0.7)',
                          transformOrigin: 'center'
                        }}
                      >
                        {template.layout_json.components.slice(0, 15).map((comp, idx) => (
                          <div
                            key={idx}
                            className="absolute border border-gray-300"
                            style={{
                              left: `${(comp.x / 794) * 100}%`,
                              top: `${(comp.y / 1123) * 100}%`,
                              width: `${(comp.width / 794) * 100}%`,
                              height: `${(comp.height / 1123) * 100}%`,
                              fontSize: '4px',
                              overflow: 'hidden',
                              backgroundColor: comp.type === 'heading' ? '#eef2ff' :
                                             comp.type === 'table' ? '#f0fdf4' :
                                             comp.type === 'line' ? '#9ca3af' : 'white'
                            }}
                          />
                        ))}
                      </div>
                      <div className="absolute bottom-2 right-2 bg-white px-2 py-1 rounded text-xs font-medium text-gray-600 shadow">
                        {template.layout_json.components.length} components
                      </div>
                    </div>
                  ) : (
                    <div className="text-6xl opacity-20">📄</div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2 truncate">
                    {template.name}
                  </h3>

                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {template.description}
                    </p>
                  )}

                  {/* Area Badges */}
                  {template.assignments && template.assignments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.assignments.map((assignment, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 text-xs rounded-full bg-${getAreaColor(assignment.area)}-100 text-${getAreaColor(assignment.area)}-700 font-medium`}
                        >
                          {assignment.area.replace(/_/g, ' ')}
                          {assignment.is_default && ' ★'}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Meta */}
                  <div className="text-xs text-gray-500 mb-3">
                    Updated {new Date(template.updated_at).toLocaleDateString()}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => navigate(`/settings/templates/${template.id}`)}
                      className="flex-1 bg-indigo-600 text-white px-2 py-1.5 rounded hover:bg-indigo-700 transition text-xs font-medium"
                      title="Edit in builder"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleRename(template)}
                      className="px-2 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition text-xs"
                      title="Rename"
                    >
                      🏷️
                    </button>
                    <button
                      onClick={() => handleEditDescription(template)}
                      className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 rounded transition text-xs"
                      title="Edit description"
                    >
                      📝
                    </button>
                    <button
                      onClick={() => handleDuplicate(template)}
                      className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 rounded transition text-xs"
                      title="Duplicate"
                    >
                      📋
                    </button>
                    <button
                      onClick={() => handleExportTemplate(template)}
                      className="px-2 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded transition text-xs"
                      title="Export for PPM library"
                    >
                      📤
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="px-2 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded transition text-xs"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>

                  {/* Set as default for assigned areas */}
                  {template.assignments && template.assignments.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-1 font-semibold uppercase">Default for:</div>
                      <div className="flex flex-wrap gap-1">
                        {template.assignments.map((a, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleToggleDefault(template, a.area)}
                            className={`px-2 py-0.5 text-[10px] rounded-full font-medium transition ${
                              a.is_default
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title={a.is_default ? `Default for ${a.area} — click to unset` : `Set as default for ${a.area}`}
                          >
                            {a.area.replace(/_/g, ' ')} {a.is_default ? '★' : '☆'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Areas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Components</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTemplates.map(template => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{template.name}</div>
                      {template.description && (
                        <div className="text-sm text-gray-500">{template.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {template.assignments && template.assignments.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {template.assignments.map((a, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700"
                            >
                              {a.area.replace(/_/g, ' ')}
                              {a.is_default && ' ★'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {template.layout_json?.components?.length || 0}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(template.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => navigate(`/settings/templates/${template.id}`)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRename(template)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Rename
                        </button>
                        <button
                          onClick={() => handleEditDescription(template)}
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Description
                        </button>
                        <button
                          onClick={() => handleDuplicate(template)}
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={() => handleExportTemplate(template)}
                          className="text-orange-600 hover:text-orange-800 text-sm"
                        >
                          Export
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default SettingsTemplates;
